/**
 * Location Manager Component.
 *
 * Enterprise-grade management for organization business locations.
 * Supports creating, editing, and deleting locations with full address
 * editing, location type selection, inventory toggle, primary designation,
 * and per-location default tax rates.
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Grid3X3,
  Layers,
  Map as MapIcon,
  MapPin,
  Pencil,
  Plus,
  Star,
  Trash2,
  Warehouse,
  X,
} from "lucide-react";
import { apiClient } from "@/api/client";
import {
  useBusinessLocations,
  useCreateLocation,
  useUpdateLocation,
  useDeleteLocation,
  useSetPrimaryLocation,
} from "@/features/locations/api";
import type { BusinessLocation } from "@/features/locations/api";
import { useOrgConfig } from "@/features/settings/api";
import {
  useStorageAreas,
  useCreateStorageArea,
  useDeleteStorageArea,
  useLocationSchemes,
  useLocationScheme,
  useCreateLocationScheme,
  useStorageAreaLocations,
  useCreateLocation as useCreateStorageLocation,
  useUpdateLocation as useUpdateStorageLocation,
  useDeleteLocation as useDeleteStorageLocation,
  useAssignSchemeToArea,
  useApplySchemeToArea,
  useRemoveSchemeFromArea,
  type StorageArea,
} from "@/features/yards/api";
import type { SegmentDefinition, LocationScheme } from "@/types/locations";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import ActionDrawer from "@/components/ui/ActionDrawer";
import LocationSchemeEditor from "@/features/yards/LocationSchemeEditor";
import YardMapOverlay from "@/features/yards/YardMapOverlay";

const LOCATION_TYPES = [
  { value: "office", label: "Office" },
  { value: "warehouse", label: "Warehouse" },
  { value: "yard", label: "Yard" },
  { value: "shop", label: "Shop" },
  { value: "showroom", label: "Showroom" },
  { value: "staging", label: "Staging" },
  { value: "receiving", label: "Receiving" },
  { value: "other", label: "Other" },
];

const typeIcon = (type: string) => {
  switch (type) {
    case "warehouse":
    case "yard":
    case "staging":
    case "receiving":
      return <Warehouse className="h-4 w-4" />;
    case "office":
    case "showroom":
      return <Building2 className="h-4 w-4" />;
    default:
      return <MapPin className="h-4 w-4" />;
  }
};

function LocationManager() {
  const { data: locations, isLoading } = useBusinessLocations();
  const { data: config } = useOrgConfig();
  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();
  const deleteMutation = useDeleteLocation();
  const setPrimaryMutation = useSetPrimaryLocation();
  const seedAttempted = useRef(false);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("warehouse");

  // Auto-seed: if no locations exist but there's a primary address in
  // business settings, create a "Headquarters" location from it.
  useEffect(() => {
    if (seedAttempted.current || isLoading || !locations || !config) return;
    if (locations.length > 0) return;

    const biz = config.business;
    const addr = biz?.primary_address;
    const hasAddress = addr?.street1 || addr?.city;
    const hasName = biz?.company_name;

    if (!hasAddress && !hasName) return;

    seedAttempted.current = true;
    createMutation.mutate(
      {
        name: hasName || "Headquarters",
        location_type: "office",
        is_primary: true,
        stores_inventory: true,
        address: hasAddress
          ? {
              street1: addr.street1 || "",
              street2: addr.street2 || "",
              city: addr.city || "",
              state: addr.state || "",
              zip: addr.zip || "",
              country: addr.country || "US",
            }
          : undefined,
        phone: biz?.phone || undefined,
        email: biz?.email || undefined,
        default_tax_rate: biz?.default_tax_rate || 0,
      },
      {
        onSuccess: (loc) => setExpandedId(loc.id),
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, isLoading, config]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const loc = await createMutation.mutateAsync({
        name: newName.trim(),
        location_type: newType,
      });
      setNewName("");
      setShowCreate(false);
      setExpandedId(loc.id);
      toast.success("Facility created", loc.name);
    } catch {
      toast.error("Failed to create facility");
    }
  };

  if (isLoading) return <LoadingSpinner className="py-8" />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            Business Facilities
          </h3>
          <p className="text-xs text-[var(--text-tertiary)]">
            Manage your organization&apos;s physical facilities. Each facility
            can have its own address, tax rate, and storage areas.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
          <Plus className="h-3.5 w-3.5" /> Add Facility
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="mb-4 rounded-[var(--radius-lg)] border border-[var(--color-interactive-primary)]/30 bg-[var(--surface-base)] p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                Facility Name
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                }}
                placeholder="e.g., Memphis Warehouse"
                className="block w-full h-9 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                Type
              </label>
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
                className="block w-full h-9 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm text-[var(--text-primary)]"
              >
                {LOCATION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreate}
              isLoading={createMutation.isPending}
              disabled={!newName.trim()}
            >
              Create Facility
            </Button>
          </div>
        </div>
      )}

      {/* Location list */}
      <div className="space-y-2">
        {locations && locations.length > 0 ? (
          locations.map((loc) => (
            <LocationCard
              key={loc.id}
              location={loc}
              isExpanded={expandedId === loc.id}
              onToggle={() =>
                setExpandedId(expandedId === loc.id ? null : loc.id)
              }
              onUpdate={(data) =>
                updateMutation.mutate(
                  { id: loc.id, data },
                  {
                    onSuccess: () => toast.success("Facility updated"),
                    onError: () => toast.error("Failed to update"),
                  },
                )
              }
              onDelete={() =>
                deleteMutation.mutate(loc.id, {
                  onSuccess: () => toast.success("Facility removed"),
                  onError: (err) => {
                    const msg = (
                      err as { response?: { data?: { detail?: string } } }
                    )?.response?.data?.detail;
                    toast.error("Cannot delete", msg || "Please try again.");
                  },
                })
              }
              onSetPrimary={() =>
                setPrimaryMutation.mutate(loc.id, {
                  onSuccess: () => toast.success("Primary facility updated"),
                  onError: () => toast.error("Failed to set primary"),
                })
              }
            />
          ))
        ) : (
          <div className="rounded-[var(--radius-lg)] border border-dashed border-[var(--border-default)] py-8 text-center">
            <MapPin className="mx-auto h-8 w-8 text-[var(--text-tertiary)]" />
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              No facilities yet
            </p>
            <p className="text-xs text-[var(--text-tertiary)]">
              Add your first facility to get started.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/** Storage area type options for the inline quick-add form. */
const AREA_TYPES = [
  { value: "parking_lot", label: "Parking Lot" },
  { value: "pallet_rack", label: "Pallet Rack" },
  { value: "floor", label: "Floor Locations" },
  { value: "shelving", label: "Shelving" },
  { value: "cold_storage", label: "Cold Storage" },
  { value: "open_yard", label: "Open Yard" },
  { value: "staging", label: "Staging Area" },
  { value: "dock", label: "Dock" },
  { value: "container", label: "Container" },
  { value: "other", label: "Other" },
];

interface YardSummary {
  id: string;
  name: string;
  section_count: number;
  is_active: boolean;
  business_location_id?: string;
}

// ---------------------------------------------------------------------------
// StorageAreasPanel — replaces LinkedYards inside each LocationCard
// ---------------------------------------------------------------------------

/**
 * Inline panel showing storage areas that belong to a business location.
 * Includes map button (one per location), area list, inline quick-add,
 * and expandable location management per area.
 */
function StorageAreasPanel({ locationId }: { locationId: string }) {
  const { data: areas, isLoading: areasLoading } = useStorageAreas(locationId);
  const { data: schemes } = useLocationSchemes();
  const createArea = useCreateStorageArea();
  const deleteArea = useDeleteStorageArea();

  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState("parking_lot");
  const [expandedAreaId, setExpandedAreaId] = useState<string | null>(null);

  // Build a scheme lookup map for displaying scheme names on area rows.
  const schemeMap = useMemo(() => {
    const m = new Map<string, string>();
    (schemes || []).forEach((s) => m.set(s.id, s.name));
    return m;
  }, [schemes]);

  const handleAddArea = async () => {
    if (!newLabel.trim()) return;
    try {
      await createArea.mutateAsync({
        label: newLabel.trim(),
        section_type: newType,
        business_location_id: locationId,
      });
      setNewLabel("");
      setShowAddForm(false);
      toast.success("Storage area created");
    } catch {
      toast.error("Failed to create storage area");
    }
  };

  const handleDeleteArea = async (area: StorageArea) => {
    if (area.asset_count > 0) {
      const ok = window.confirm(
        `"${area.label}" has ${area.asset_count} asset(s) assigned. Deleting will un-assign them. Continue?`,
      );
      if (!ok) return;
    }
    try {
      await deleteArea.mutateAsync(area.id);
      toast.success("Storage area deleted");
    } catch {
      toast.error("Failed to delete storage area");
    }
  };

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Grid3X3 className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
            Storage Areas
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1 text-[10px] font-medium text-[var(--color-interactive-primary)] hover:underline"
        >
          <Plus className="h-3 w-3" /> Add Area
        </button>
      </div>

      {/* Area list */}
      {areasLoading ? (
        <LoadingSpinner className="py-4" />
      ) : areas && areas.length > 0 ? (
        <div className="space-y-1.5">
          {areas.map((area) => (
            <StorageAreaRow
              key={area.id}
              area={area}
              schemeName={
                area.location_scheme_id
                  ? schemeMap.get(area.location_scheme_id) || "Unknown"
                  : null
              }
              schemes={schemes || []}
              isExpanded={expandedAreaId === area.id}
              onToggle={() =>
                setExpandedAreaId(expandedAreaId === area.id ? null : area.id)
              }
              onDelete={() => handleDeleteArea(area)}
            />
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-[var(--text-tertiary)] italic">
          No storage areas yet. Add one to start managing inventory locations.
        </p>
      )}

      {/* Inline quick-add form */}
      {showAddForm && (
        <div className="mt-2 flex items-end gap-2 rounded-[var(--radius-sm)] border border-[var(--color-interactive-primary)]/30 bg-[var(--surface-sunken)] p-2">
          <div className="flex-1">
            <label className="block text-[10px] text-[var(--text-tertiary)] mb-0.5">
              Label
            </label>
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddArea();
                if (e.key === "Escape") setShowAddForm(false);
              }}
              placeholder="e.g., Row A, Parking Lot"
              className="w-full h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
              autoFocus
            />
          </div>
          <div className="w-28">
            <label className="block text-[10px] text-[var(--text-tertiary)] mb-0.5">
              Type
            </label>
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              className="w-full h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-1.5 text-xs text-[var(--text-primary)]"
            >
              {AREA_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleAddArea}
            disabled={!newLabel.trim() || createArea.isPending}
            className="h-7 rounded-[var(--radius-sm)] px-3 text-[10px] font-medium bg-[var(--color-interactive-primary)] text-white hover:opacity-90 disabled:opacity-40"
          >
            Add
          </button>
          <button
            onClick={() => setShowAddForm(false)}
            className="h-7 px-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Label Preview Helpers (mirrors backend cross-product logic)
// ---------------------------------------------------------------------------

function expandSegment(seg: SegmentDefinition): string[] {
  const prefix = seg.prefix || "";
  if (seg.type === "number") {
    const start =
      typeof seg.start === "number"
        ? seg.start
        : parseInt(String(seg.start ?? "1"));
    const end =
      typeof seg.end === "number" ? seg.end : parseInt(String(seg.end ?? "1"));
    const pad = seg.padding ?? 0;
    const result: string[] = [];
    for (let i = start; i <= end; i++)
      result.push(`${prefix}${String(i).padStart(pad, "0")}`);
    return result;
  }
  if (seg.type === "letter") {
    const startCode = String(seg.start ?? "A")
      .toUpperCase()
      .charCodeAt(0);
    const endCode = String(seg.end ?? "A")
      .toUpperCase()
      .charCodeAt(0);
    const result: string[] = [];
    for (let c = startCode; c <= endCode; c++)
      result.push(`${prefix}${String.fromCharCode(c)}`);
    return result;
  }
  if (seg.type === "fixed")
    return (seg.values ?? []).map((v) => `${prefix}${v}`);
  return [];
}

function generatePreviewLabels(
  segments: SegmentDefinition[],
  separator: string,
): string[] {
  if (segments.length === 0) return [];
  const expanded = segments.map(expandSegment);
  if (expanded.some((e) => e.length === 0)) return [];
  const MAX = 10001;
  const result: string[] = [];
  const indices = new Array(expanded.length).fill(0) as number[];
  while (result.length < MAX) {
    result.push(indices.map((idx, i) => expanded[i]![idx]!).join(separator));
    let carry = true;
    for (let i = indices.length - 1; i >= 0 && carry; i--) {
      indices[i]!++;
      if (indices[i]! < expanded[i]!.length) carry = false;
      else indices[i] = 0;
    }
    if (carry) break;
  }
  return result;
}

// ---------------------------------------------------------------------------
// SchemeGenerationPanel — configure ranges and generate locations from scheme
// ---------------------------------------------------------------------------

function SchemeGenerationPanel({
  areaId,
  scheme,
  existingLocationCount,
  onChangeScheme,
  onRemoveScheme,
}: {
  areaId: string;
  scheme: LocationScheme;
  existingLocationCount: number;
  onChangeScheme: () => void;
  onRemoveScheme: () => void;
}) {
  const applyScheme = useApplySchemeToArea();

  const [overrides, setOverrides] = useState<
    Array<{
      start: number | string | undefined;
      end: number | string | undefined;
    }>
  >(() =>
    (scheme.segments as SegmentDefinition[]).map((seg) => ({
      start: seg.start ?? undefined,
      end: seg.end ?? undefined,
    })),
  );
  const [capacity, setCapacity] = useState(1);

  const mergedSegments = useMemo(
    () =>
      (scheme.segments as SegmentDefinition[]).map((seg, i) => ({
        ...seg,
        start: overrides[i]?.start ?? seg.start,
        end: overrides[i]?.end ?? seg.end,
      })),
    [scheme.segments, overrides],
  );

  const allRangesSet = mergedSegments.every(
    (seg) => seg.type === "fixed" || (seg.start != null && seg.end != null),
  );

  const previewLabels = useMemo(() => {
    if (!allRangesSet) return [];
    return generatePreviewLabels(mergedSegments, scheme.separator);
  }, [mergedSegments, scheme.separator, allRangesSet]);

  const totalCount = previewLabels.length;

  const handleGenerate = async () => {
    if (!allRangesSet) return;
    if (
      existingLocationCount > 0 &&
      !window.confirm(
        `This will replace ${existingLocationCount} existing location(s). Assets keep their area assignment but lose specific location assignments. Continue?`,
      )
    )
      return;

    const segmentOverrides = overrides
      .map((ov, index) => ({ index, start: ov.start, end: ov.end }))
      .filter((ov) => ov.start != null || ov.end != null);

    try {
      const result = await applyScheme.mutateAsync({
        areaId,
        scheme_id: scheme.id,
        location_capacity: capacity,
        segment_overrides: segmentOverrides,
      });
      toast.success(
        "Locations generated",
        `${result.location_count} locations created`,
      );
    } catch {
      toast.error("Failed to generate locations");
    }
  };

  const updateOverride = (
    index: number,
    field: "start" | "end",
    raw: string,
  ) => {
    setOverrides((prev) => {
      const next = [...prev];
      const seg = (scheme.segments as SegmentDefinition[])[index]!;
      if (seg.type === "number") {
        next[index] = {
          ...next[index]!,
          [field]: raw === "" ? undefined : parseInt(raw) || 0,
        };
      } else {
        next[index] = {
          ...next[index]!,
          [field]: raw === "" ? undefined : raw.toUpperCase(),
        };
      }
      return next;
    });
  };

  return (
    <div className="border-t border-[var(--border-subtle)] px-3 py-3">
      {/* Scheme header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Layers className="h-3.5 w-3.5 text-[var(--color-interactive-primary)]" />
          <span className="text-xs font-medium text-[var(--text-primary)]">
            Scheme: {scheme.name}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onChangeScheme}
            className="text-[10px] text-[var(--color-interactive-primary)] hover:underline"
          >
            Change
          </button>
          <button
            onClick={onRemoveScheme}
            className="text-[10px] text-[var(--text-danger)] hover:underline"
          >
            Remove
          </button>
        </div>
      </div>

      {/* Generation parameters */}
      <div className="rounded-[var(--radius-sm)] border border-[var(--border-subtle)] bg-[var(--surface-sunken)] p-3 space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          Generation Parameters
        </p>

        <div className="grid grid-cols-2 gap-3">
          {(scheme.segments as SegmentDefinition[]).map((seg, i) =>
            seg.type === "fixed" ? (
              <div key={i} className="space-y-1">
                <label className="text-[10px] text-[var(--text-tertiary)]">
                  {seg.name} <span className="opacity-60">(fixed)</span>
                </label>
                <p className="text-xs text-[var(--text-secondary)]">
                  {(seg.values ?? []).join(", ")}
                </p>
              </div>
            ) : (
              <div key={i} className="space-y-1">
                <label className="text-[10px] text-[var(--text-tertiary)]">
                  {seg.name}{" "}
                  <span className="opacity-60">
                    (
                    {seg.type === "number"
                      ? `number, pad ${seg.padding ?? 0}`
                      : "letter"}
                    )
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <span className="text-[9px] text-[var(--text-tertiary)]">
                      Start
                    </span>
                    <input
                      type={seg.type === "number" ? "number" : "text"}
                      value={overrides[i]?.start ?? ""}
                      onChange={(e) =>
                        updateOverride(i, "start", e.target.value)
                      }
                      maxLength={seg.type === "letter" ? 1 : undefined}
                      className="w-full h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder={seg.type === "number" ? "1" : "A"}
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-[9px] text-[var(--text-tertiary)]">
                      End
                    </span>
                    <input
                      type={seg.type === "number" ? "number" : "text"}
                      value={overrides[i]?.end ?? ""}
                      onChange={(e) => updateOverride(i, "end", e.target.value)}
                      maxLength={seg.type === "letter" ? 1 : undefined}
                      className="w-full h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder={seg.type === "number" ? "50" : "Z"}
                    />
                  </div>
                </div>
              </div>
            ),
          )}
        </div>

        {/* Capacity */}
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-[var(--text-tertiary)] whitespace-nowrap">
            Capacity per location:
          </label>
          <input
            type="number"
            value={capacity}
            onChange={(e) =>
              setCapacity(Math.max(1, parseInt(e.target.value) || 1))
            }
            min={1}
            className="w-16 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-center text-[var(--text-primary)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
        </div>

        {/* Preview */}
        {allRangesSet && totalCount > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
              Preview ({totalCount.toLocaleString()} location
              {totalCount !== 1 ? "s" : ""})
            </p>
            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
              {previewLabels.slice(0, 20).map((label, i) => (
                <span
                  key={i}
                  className="rounded px-1.5 py-0.5 text-[10px] font-mono bg-[var(--surface-base)] border border-[var(--border-subtle)] text-[var(--text-secondary)]"
                >
                  {label}
                </span>
              ))}
              {totalCount > 20 && (
                <>
                  <span className="text-[10px] text-[var(--text-tertiary)] self-center">
                    …
                  </span>
                  {previewLabels.slice(-3).map((label, i) => (
                    <span
                      key={`last-${i}`}
                      className="rounded px-1.5 py-0.5 text-[10px] font-mono bg-[var(--surface-base)] border border-[var(--border-subtle)] text-[var(--text-secondary)]"
                    >
                      {label}
                    </span>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {!allRangesSet && (
          <p className="text-[10px] text-[var(--text-tertiary)] italic">
            Fill in start and end values for all segments to preview locations.
          </p>
        )}

        {/* Generate button */}
        <div className="flex justify-end pt-1">
          <Button
            onClick={handleGenerate}
            disabled={
              !allRangesSet || totalCount === 0 || applyScheme.isPending
            }
            size="sm"
          >
            {applyScheme.isPending
              ? "Generating…"
              : existingLocationCount > 0
                ? `Regenerate ${totalCount.toLocaleString()} Locations`
                : `Generate ${totalCount.toLocaleString()} Locations`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StorageAreaRow — one row per storage area, expandable to show locations
// ---------------------------------------------------------------------------

/**
 * Compact row for a single storage area within a business location.
 * Shows label, type, scheme, metrics. Expands to show scheme generation
 * panel (if scheme assigned) and location list with manual entry.
 */
function StorageAreaRow({
  area,
  schemeName,
  schemes,
  isExpanded,
  onToggle,
  onDelete,
}: {
  area: StorageArea;
  schemeName: string | null;
  schemes: LocationScheme[];
  isExpanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const [showSchemePicker, setShowSchemePicker] = useState(false);
  const [showCreateScheme, setShowCreateScheme] = useState(false);

  const assignScheme = useAssignSchemeToArea();
  const removeScheme = useRemoveSchemeFromArea();
  const createScheme = useCreateLocationScheme();

  const { data: schemeDetail } = useLocationScheme(
    isExpanded && area.location_scheme_id ? area.location_scheme_id : undefined,
  );

  const handleAssignScheme = async (schemeId: string) => {
    try {
      await assignScheme.mutateAsync({ areaId: area.id, scheme_id: schemeId });
      toast.success("Scheme assigned");
      setShowSchemePicker(false);
    } catch {
      toast.error("Failed to assign scheme");
    }
  };

  const handleRemoveScheme = async () => {
    if (
      area.location_count > 0 &&
      !window.confirm(
        `This will remove ${area.location_count} location(s) and their scheme assignment. Continue?`,
      )
    )
      return;
    try {
      await removeScheme.mutateAsync(area.id);
      toast.success("Scheme removed");
    } catch {
      toast.error("Failed to remove scheme");
    }
  };

  const handleCreateAndAssign = async (data: {
    name: string;
    description?: string;
    segments: SegmentDefinition[];
    separator: string;
  }) => {
    try {
      const newScheme = await createScheme.mutateAsync(data);
      await assignScheme.mutateAsync({
        areaId: area.id,
        scheme_id: newScheme.id,
      });
      setShowCreateScheme(false);
      toast.success("Scheme created and assigned");
    } catch {
      toast.error("Failed to create scheme");
    }
  };

  return (
    <>
      <div
        className={`rounded-[var(--radius-sm)] border transition-colors ${
          isExpanded
            ? "border-[var(--border-default)] bg-[var(--surface-base)]"
            : "border-[var(--border-subtle)] bg-[var(--surface-sunken)]/50"
        }`}
      >
        {/* Collapsed header */}
        <div className="flex items-center justify-between px-3 py-2">
          <div
            className="flex items-center gap-2 flex-1 cursor-pointer min-w-0"
            onClick={onToggle}
          >
            {isExpanded ? (
              <ChevronDown className="h-3 w-3 text-[var(--text-tertiary)] shrink-0" />
            ) : (
              <ChevronRight className="h-3 w-3 text-[var(--text-tertiary)] shrink-0" />
            )}
            <Grid3X3 className="h-3.5 w-3.5 text-[var(--text-tertiary)] shrink-0" />
            <span className="text-xs font-medium text-[var(--text-primary)] truncate">
              {area.label}
            </span>
            <span className="shrink-0 rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wide font-medium bg-[var(--surface-sunken)] text-[var(--text-tertiary)]">
              {area.section_type}
            </span>
            {schemeName && (
              <span className="shrink-0 text-[10px] text-[var(--color-interactive-primary)] truncate">
                <Layers className="inline h-2.5 w-2.5 mr-0.5" />
                {schemeName}
              </span>
            )}
            <span className="shrink-0 text-[10px] text-[var(--text-tertiary)]">
              {area.location_count} loc{area.location_count !== 1 ? "s" : ""}
            </span>
            <span className="shrink-0 text-[10px] text-[var(--text-tertiary)]">
              {area.asset_count} asset{area.asset_count !== 1 ? "s" : ""}
            </span>
          </div>
          <button
            onClick={onDelete}
            className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)] shrink-0"
            title="Delete storage area"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <>
            {/* Scheme generation panel (when a scheme is assigned) */}
            {area.location_scheme_id && schemeDetail && (
              <SchemeGenerationPanel
                areaId={area.id}
                scheme={schemeDetail}
                existingLocationCount={area.location_count}
                onChangeScheme={() => setShowSchemePicker(true)}
                onRemoveScheme={handleRemoveScheme}
              />
            )}

            {/* Location list + manual entry + scheme promotion */}
            <LocationListPanel
              areaId={area.id}
              hasScheme={!!area.location_scheme_id}
              schemes={schemes}
              currentSchemeId={area.location_scheme_id}
              showSchemePicker={showSchemePicker}
              onToggleSchemePicker={() =>
                setShowSchemePicker(!showSchemePicker)
              }
              onCloseSchemePicker={() => setShowSchemePicker(false)}
              onAssignScheme={handleAssignScheme}
              onCreateNew={() => {
                setShowSchemePicker(false);
                setShowCreateScheme(true);
              }}
            />
          </>
        )}
      </div>

      {/* Inline scheme creation drawer */}
      <ActionDrawer
        isOpen={showCreateScheme}
        onClose={() => setShowCreateScheme(false)}
        title="Create Location Scheme"
        description="Define a reusable labeling template for storage locations."
        icon={Layers}
        size="xl"
      >
        <LocationSchemeEditor
          onSave={handleCreateAndAssign}
          onCancel={() => setShowCreateScheme(false)}
          isSaving={createScheme.isPending || assignScheme.isPending}
        />
      </ActionDrawer>
    </>
  );
}

// ---------------------------------------------------------------------------
// LocationListPanel — locations list + scheme promotion + manual add
// ---------------------------------------------------------------------------

function LocationListPanel({
  areaId,
  hasScheme,
  schemes,
  currentSchemeId,
  showSchemePicker,
  onToggleSchemePicker,
  onCloseSchemePicker,
  onAssignScheme,
  onCreateNew,
}: {
  areaId: string;
  hasScheme: boolean;
  schemes: LocationScheme[];
  currentSchemeId: string | null;
  showSchemePicker: boolean;
  onToggleSchemePicker: () => void;
  onCloseSchemePicker: () => void;
  onAssignScheme: (schemeId: string) => void;
  onCreateNew: () => void;
}) {
  const { data: locationsPage, isLoading } = useStorageAreaLocations(areaId);
  const locations = locationsPage?.items ?? [];
  const createLoc = useCreateStorageLocation();
  const updateLoc = useUpdateStorageLocation();
  const deleteLoc = useDeleteStorageLocation();

  const [newLocLabel, setNewLocLabel] = useState("");
  const [newLocCapacity, setNewLocCapacity] = useState(1);
  const [showManualAdd, setShowManualAdd] = useState(false);

  const locationCount = locationsPage?.total ?? 0;
  const isTruncated = locationCount > locations.length;
  const isEmpty = !isLoading && locationCount === 0;

  const handleAddLocation = async () => {
    if (!newLocLabel.trim()) return;
    try {
      await createLoc.mutateAsync({
        areaId,
        label: newLocLabel.trim(),
        capacity: newLocCapacity,
      });
      setNewLocLabel("");
      setNewLocCapacity(1);
    } catch {
      toast.error("Failed to create location");
    }
  };

  const handleDeleteLocation = async (
    locationId: string,
    assetCount: number,
    label: string,
  ) => {
    if (
      assetCount > 0 &&
      !window.confirm(
        `"${label}" has ${assetCount} asset(s). They will keep their area assignment but lose this specific location. Continue?`,
      )
    )
      return;
    try {
      await deleteLoc.mutateAsync({ areaId, locationId });
    } catch {
      toast.error("Failed to delete location");
    }
  };

  return (
    <div className="border-t border-[var(--border-subtle)] px-3 py-2">
      {/* Scheme promotion — shown when no scheme is assigned */}
      {!hasScheme && (
        <div className="mb-3 rounded-[var(--radius-sm)] border border-[var(--color-interactive-primary)]/20 bg-[var(--color-interactive-primary)]/5 p-3">
          <div className="flex items-start gap-2">
            <Layers className="h-4 w-4 text-[var(--color-interactive-primary)] shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <div>
                <p className="text-xs font-medium text-[var(--text-primary)]">
                  Generate from Scheme
                  <span className="ml-1.5 text-[9px] font-semibold uppercase tracking-wider text-[var(--color-interactive-primary)]">
                    Recommended
                  </span>
                </p>
                <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">
                  Select a reusable template to auto-generate labeled storage
                  locations.
                </p>
              </div>
              <div className="relative flex items-center gap-2">
                <button
                  onClick={onToggleSchemePicker}
                  className="flex items-center gap-1.5 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-xs text-[var(--text-primary)] hover:border-[var(--border-strong)]"
                >
                  <Layers className="h-3 w-3 text-[var(--text-tertiary)]" />
                  {currentSchemeId ? "Change scheme…" : "Select a scheme…"}
                </button>
                <button
                  onClick={onCreateNew}
                  className="flex items-center gap-1 text-[10px] font-medium text-[var(--color-interactive-primary)] hover:underline"
                >
                  <Plus className="h-3 w-3" /> Create New
                </button>
                {showSchemePicker && (
                  <SchemePickerPopover
                    schemes={schemes}
                    currentSchemeId={currentSchemeId}
                    onSelect={onAssignScheme}
                    onClose={onCloseSchemePicker}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Locations list */}
      {isLoading ? (
        <LoadingSpinner className="py-2" />
      ) : locationCount > 0 ? (
        <div className="space-y-1">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
              Locations ({locationCount})
            </p>
            <button
              onClick={() => setShowManualAdd(!showManualAdd)}
              className="flex items-center gap-1 text-[10px] text-[var(--color-interactive-primary)] hover:underline"
            >
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
          <div className="flex items-center gap-2 px-1 text-[9px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
            <span className="flex-1">Label</span>
            <span className="w-16 text-center">Capacity</span>
            <span className="w-12 text-center">Active</span>
            <span className="w-14 text-center">Assets</span>
            <span className="w-6" />
          </div>
          {isTruncated && (
            <p className="px-1 text-[10px] text-[var(--text-tertiary)]">
              Showing first {locations.length} of{" "}
              {locationCount.toLocaleString()}.
            </p>
          )}
          {locations.map((loc) => (
            <div
              key={loc.id}
              className="flex items-center gap-2 rounded-[var(--radius-sm)] px-1 py-1 hover:bg-[var(--surface-sunken)]/50"
            >
              <input
                type="text"
                defaultValue={loc.label}
                onBlur={(e) => {
                  if (e.target.value !== loc.label && e.target.value.trim()) {
                    updateLoc.mutate({
                      areaId,
                      locationId: loc.id,
                      data: { label: e.target.value.trim() },
                    });
                  }
                }}
                className="flex-1 h-6 rounded border border-transparent hover:border-[var(--border-default)] focus:border-[var(--border-default)] bg-transparent px-1 text-xs text-[var(--text-primary)] focus:outline-none"
              />
              <input
                type="number"
                defaultValue={loc.capacity}
                min={1}
                onBlur={(e) => {
                  const val = parseInt(e.target.value) || 1;
                  if (val !== loc.capacity) {
                    updateLoc.mutate({
                      areaId,
                      locationId: loc.id,
                      data: { capacity: val },
                    });
                  }
                }}
                className="w-16 h-6 rounded border border-transparent hover:border-[var(--border-default)] focus:border-[var(--border-default)] bg-transparent px-1 text-xs text-center text-[var(--text-primary)] focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <div className="w-12 flex justify-center">
                <input
                  type="checkbox"
                  checked={loc.is_active}
                  onChange={(e) =>
                    updateLoc.mutate({
                      areaId,
                      locationId: loc.id,
                      data: { is_active: e.target.checked },
                    })
                  }
                  className="h-3.5 w-3.5 accent-[var(--color-interactive-primary)]"
                />
              </div>
              <span className="w-14 text-center text-[10px] text-[var(--text-tertiary)]">
                {loc.asset_count}
              </span>
              <button
                onClick={() =>
                  handleDeleteLocation(loc.id, loc.asset_count, loc.label)
                }
                className="w-6 flex justify-center text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                title="Delete location"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      ) : !hasScheme ? (
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-[var(--text-tertiary)] italic">
            No locations yet. Use a scheme above or add manually.
          </p>
          <button
            onClick={() => setShowManualAdd(true)}
            className="flex items-center gap-1 text-[10px] text-[var(--color-interactive-primary)] hover:underline"
          >
            <Plus className="h-3 w-3" /> Add Manually
          </button>
        </div>
      ) : null}

      {/* Manual add form — shown below locations or when explicitly toggled */}
      {(showManualAdd || (isEmpty && hasScheme)) && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="text"
            value={newLocLabel}
            onChange={(e) => setNewLocLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddLocation();
            }}
            placeholder="Location label..."
            className="flex-1 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
          />
          <input
            type="number"
            value={newLocCapacity}
            onChange={(e) => setNewLocCapacity(parseInt(e.target.value) || 1)}
            min={1}
            className="w-16 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-center text-[var(--text-primary)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            title="Capacity"
          />
          <button
            onClick={handleAddLocation}
            disabled={!newLocLabel.trim() || createLoc.isPending}
            className="flex h-7 items-center gap-1 rounded-[var(--radius-sm)] px-2 text-[10px] font-medium text-[var(--color-interactive-primary)] hover:bg-[var(--surface-sunken)] disabled:opacity-40"
          >
            <Plus className="h-3 w-3" /> Add
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SchemePickerPopover — dropdown to select an existing scheme
// ---------------------------------------------------------------------------

function SchemePickerPopover({
  schemes,
  currentSchemeId,
  onSelect,
  onClose,
}: {
  schemes: LocationScheme[];
  currentSchemeId: string | null;
  onSelect: (schemeId: string) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div className="fixed inset-0 z-10" onClick={onClose} />
      <div className="absolute left-0 top-8 z-20 w-56 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] shadow-lg py-1 max-h-48 overflow-y-auto">
        <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          Select Scheme
        </p>
        {schemes.length > 0 ? (
          schemes.map((s) => (
            <button
              key={s.id}
              onClick={() => onSelect(s.id)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--surface-sunken)] ${
                s.id === currentSchemeId
                  ? "text-[var(--color-interactive-primary)] font-medium"
                  : "text-[var(--text-primary)]"
              }`}
            >
              {s.name}
              {s.id === currentSchemeId && " (current)"}
            </button>
          ))
        ) : (
          <p className="px-3 py-1.5 text-xs text-[var(--text-tertiary)] italic">
            No schemes defined yet. Create one to get started.
          </p>
        )}
      </div>
    </>
  );
}

/** Individual location card with expandable detail. */
function LocationCard({
  location,
  isExpanded,
  onToggle,
  onUpdate,
  onDelete,
  onSetPrimary,
}: {
  location: BusinessLocation;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate: (data: Record<string, unknown>) => void;
  onDelete: () => void;
  onSetPrimary: () => void;
}) {
  const addr = location.address || {};
  const hasAddress = addr.street1 || addr.city;

  // Inventory & Storage (storage areas + facility map) opens in its own
  // full-screen overlay so it isn't buried inside the business-details card.
  const [showStorage, setShowStorage] = useState(false);

  return (
    <div
      className={`rounded-[var(--radius-lg)] border transition-colors ${
        isExpanded
          ? "border-[var(--color-interactive-primary)]/30 bg-[var(--surface-base)] shadow-sm"
          : "border-[var(--border-subtle)] hover:border-[var(--border-default)]"
      }`}
    >
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        onClick={onToggle}
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-[var(--text-tertiary)] shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-[var(--text-tertiary)] shrink-0" />
        )}
        <div className="flex items-center gap-2 text-[var(--text-secondary)]">
          {typeIcon(location.location_type)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[var(--text-primary)]">
              {location.name}
            </span>
            {location.is_primary && (
              <Badge variant="info">
                <Star className="h-2.5 w-2.5 mr-0.5" /> Primary
              </Badge>
            )}
          </div>
          {hasAddress && (
            <p className="text-xs text-[var(--text-tertiary)] truncate mt-0.5">
              {[addr.city, addr.state].filter(Boolean).join(", ")}
              {addr.zip ? ` ${addr.zip}` : ""}
            </p>
          )}
        </div>
        {location.stores_inventory && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowStorage(true);
            }}
            className="flex items-center gap-1.5 shrink-0 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 py-1 text-[11px] font-medium text-[var(--text-secondary)] hover:border-[var(--border-strong)] hover:text-[var(--text-primary)] transition-colors"
          >
            <Warehouse className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
            Inventory &amp; Storage
          </button>
        )}
        <label
          className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={location.stores_inventory}
            onChange={(e) => onUpdate({ stores_inventory: e.target.checked })}
            className="h-3.5 w-3.5 accent-[var(--color-interactive-primary)]"
          />
          <span className="text-[var(--text-secondary)]">Inventory</span>
        </label>
        <Badge variant="default">{location.location_type}</Badge>
      </div>

      {/* Inventory & Storage overlay (storage areas + facility map) */}
      <FacilityStorageOverlay
        isOpen={showStorage}
        onClose={() => setShowStorage(false)}
        location={location}
      />

      {/* Expanded detail */}
      {isExpanded && (
        <div className="border-t border-[var(--border-subtle)]">
          {/* ── SECTION 1: LOCATION DETAILS ── */}
          <div className="px-4 py-4 space-y-4">
            <div className="flex items-center gap-2 pb-2 mb-1 border-b border-[var(--border-subtle)]">
              <Building2 className="h-4 w-4 text-[var(--text-tertiary)]" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                Facility Details
              </span>
            </div>

            {/* Name & Type */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                  Name
                </label>
                <input
                  type="text"
                  defaultValue={location.name}
                  onBlur={(e) => {
                    if (e.target.value !== location.name)
                      onUpdate({ name: e.target.value });
                  }}
                  className="block w-full h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                  Type
                </label>
                <select
                  defaultValue={location.location_type}
                  onChange={(e) => onUpdate({ location_type: e.target.value })}
                  className="block w-full h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm text-[var(--text-primary)]"
                >
                  {LOCATION_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                Address
              </label>
              <div className="space-y-1.5">
                <input
                  type="text"
                  defaultValue={addr.street1 || ""}
                  placeholder="Street address"
                  onBlur={(e) =>
                    onUpdate({
                      address: { ...addr, street1: e.target.value },
                    })
                  }
                  className="block w-full h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                />
                <input
                  type="text"
                  defaultValue={addr.street2 || ""}
                  placeholder="Suite, unit, etc."
                  onBlur={(e) =>
                    onUpdate({
                      address: { ...addr, street2: e.target.value },
                    })
                  }
                  className="block w-full h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    defaultValue={addr.city || ""}
                    placeholder="City"
                    onBlur={(e) =>
                      onUpdate({
                        address: { ...addr, city: e.target.value },
                      })
                    }
                    className="flex-1 h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                  />
                  <input
                    type="text"
                    defaultValue={addr.state || ""}
                    placeholder="State"
                    onBlur={(e) =>
                      onUpdate({
                        address: { ...addr, state: e.target.value },
                      })
                    }
                    className="w-20 h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                  />
                  <input
                    type="text"
                    defaultValue={addr.zip || ""}
                    placeholder="ZIP"
                    onBlur={(e) =>
                      onUpdate({
                        address: { ...addr, zip: e.target.value },
                      })
                    }
                    className="w-24 h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                  />
                </div>
              </div>
            </div>

            {/* Contact & Tax */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                  Phone
                </label>
                <input
                  type="text"
                  defaultValue={location.phone || ""}
                  placeholder="(555) 123-4567"
                  onBlur={(e) => onUpdate({ phone: e.target.value || null })}
                  className="block w-full h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                  Email
                </label>
                <input
                  type="email"
                  defaultValue={location.email || ""}
                  placeholder="warehouse@company.com"
                  onBlur={(e) => onUpdate({ email: e.target.value || null })}
                  className="block w-full h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                  Default Tax Rate (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  defaultValue={
                    Number(location.default_tax_rate) > 0
                      ? (Number(location.default_tax_rate) * 100).toFixed(2)
                      : ""
                  }
                  placeholder="0.00"
                  onBlur={(e) => {
                    const pct = parseFloat(e.target.value) || 0;
                    onUpdate({ default_tax_rate: pct / 100 });
                  }}
                  className="block w-full h-8 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-sm text-right text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>

            {/* Location-level actions */}
            <div className="flex items-center justify-end pt-2 border-t border-[var(--border-subtle)]">
              <div className="flex items-center gap-2">
                {!location.is_primary && (
                  <button
                    onClick={onSetPrimary}
                    className="flex items-center gap-1 text-[11px] font-medium text-[var(--color-interactive-primary)] hover:underline"
                  >
                    <Star className="h-3 w-3" /> Set as Primary
                  </button>
                )}
                {!location.is_primary && (
                  <button
                    onClick={onDelete}
                    className="flex items-center gap-1 text-[11px] text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Full-screen overlay for a facility's Inventory & Storage — the storage-area
 * list plus the facility map. Opened from a control on the facility row so it
 * isn't buried inside the business-details card. Encapsulates the map wiring
 * (create/open the yard map) that previously lived in LocationCard.
 */
function FacilityStorageOverlay({
  isOpen,
  onClose,
  location,
}: {
  isOpen: boolean;
  onClose: () => void;
  location: BusinessLocation;
}) {
  const qc = useQueryClient();
  const [mapOverlayYardId, setMapOverlayYardId] = useState<string | null>(null);
  const [mapOverlayYardName, setMapOverlayYardName] = useState("");

  const { data: allYards } = useQuery<YardSummary[]>({
    queryKey: ["yards"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/yards")).data as YardSummary[],
    enabled: isOpen,
  });

  const linkedYard = useMemo(
    () => (allYards || []).find((y) => y.business_location_id === location.id),
    [allYards, location.id],
  );

  const handleMapClick = async () => {
    if (linkedYard) {
      setMapOverlayYardId(linkedYard.id);
      setMapOverlayYardName(linkedYard.name);
    } else {
      try {
        const res = await apiClient.post("/api/v1/yards", {
          name: `${location.name} Map`,
          business_location_id: location.id,
        });
        qc.invalidateQueries({ queryKey: ["yards"] });
        const yard = res.data as YardSummary;
        setMapOverlayYardId(yard.id);
        setMapOverlayYardName(yard.name);
        toast.success("Facility map created");
      } catch {
        toast.error("Failed to create facility map");
      }
    }
  };

  // Escape closes this overlay — but only when the map editor isn't stacked on
  // top of it (the map handles its own Escape).
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !mapOverlayYardId) onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose, mapOverlayYardId]);

  // Lock body scroll while open; re-assert after the map editor closes.
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, mapOverlayYardId]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[50]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px]" />

      {/* Full-screen content panel */}
      <div
        className="fixed inset-3 flex flex-col rounded-[var(--radius-lg)] bg-[var(--surface-base)] border border-[var(--border-default)] shadow-2xl overflow-hidden"
        style={{ animation: "scaleIn 0.2s var(--ease-smooth) forwards" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)] bg-[var(--surface-base)]">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-interactive-primary)]/10">
              <Warehouse className="h-5 w-5 text-[var(--color-interactive-primary)]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[var(--text-primary)]">
                Inventory &amp; Storage
              </h2>
              <p className="text-xs text-[var(--text-secondary)]">
                {location.name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <p className="mr-1 hidden text-xs text-[var(--text-tertiary)] sm:block">
              Changes save automatically
            </p>
            <Button size="sm" onClick={onClose}>
              <Check className="h-4 w-4" /> Done
            </Button>
            <button
              onClick={onClose}
              aria-label="Close"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-tertiary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--text-primary)] transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Facility map access */}
          <div className="flex items-center justify-between pb-2 border-b border-[var(--border-subtle)]">
            <div className="flex items-center gap-2">
              <MapIcon className="h-4 w-4 text-[var(--text-tertiary)]" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                Facility Map
              </span>
            </div>
            <button
              onClick={handleMapClick}
              className="flex items-center gap-1 text-xs font-medium text-[var(--color-interactive-primary)] hover:underline"
            >
              {linkedYard ? (
                <>
                  <Pencil className="h-3.5 w-3.5" /> Edit Map
                </>
              ) : (
                <>
                  <MapIcon className="h-3.5 w-3.5" /> Add Map
                </>
              )}
            </button>
          </div>

          <StorageAreasPanel locationId={location.id} />
        </div>
      </div>

      {/* Facility Map Editor, stacked above (z-[60] > z-[50]) */}
      {mapOverlayYardId && (
        <YardMapOverlay
          isOpen={true}
          onClose={() => setMapOverlayYardId(null)}
          yardId={mapOverlayYardId}
          yardName={mapOverlayYardName}
        />
      )}
    </div>,
    document.body,
  );
}

export default LocationManager;
